import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import AppHeader from './AppHeader';
import './AdminPanel.css';

// Backend API URL - configurable per environment
const BACKEND_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://app.getphotoshoot.ai'
    : 'http://localhost:6779');

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, setUser, fetchUserData } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rechargingUserId, setRechargingUserId] = useState(null);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeDescription, setRechargeDescription] = useState('');
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [walletInfo, setWalletInfo] = useState({ credits: 0, totalSpent: 0 });
  const [downloadingWallet, setDownloadingWallet] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Check if user is authenticated and is admin
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    // Check admin access
    checkAdminAccess();
    fetchUserWallet();
  }, [navigate, isAuthenticated]);

  // Check admin access by calling admin endpoint
  const checkAdminAccess = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/');
        return;
      }

      // Try to fetch users - if successful, user is admin
      await fetchUsers();
    } catch (err) {
      console.error('Admin access check failed:', err);
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
    }
  };

  // Fetch user wallet info
  const fetchUserWallet = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`${BACKEND_URL}/api/wallet`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success) {
        setWalletInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success) {
        setUsers(response.data.data || []);
      } else {
        throw new Error(response.data?.error || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || 'Failed to fetch users. Admin access required.');
      if (err.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle login as user
  const handleLoginAs = async (targetUserId) => {
    // Confirm before switching users
    if (!window.confirm('Are you sure you want to login as this user? You will be logged out of the admin account.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/login-as/${targetUserId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data?.success) {
        // Update token in localStorage
        localStorage.setItem('authToken', response.data.token);
        
        // Update auth context with new user data
        if (setUser) {
          setUser(response.data.user);
        }
        
        // Fetch updated user data to ensure wallet info is current
        if (fetchUserData) {
          await fetchUserData();
        }
        
        toast.success(`Successfully logged in as ${response.data.user.email || response.data.user.name}`);
        
        // Navigate to home page
        navigate('/');
      } else {
        throw new Error(response.data?.error || 'Failed to login as user');
      }
    } catch (err) {
      console.error('Error logging in as user:', err);
      toast.error(err.response?.data?.error || 'Failed to login as user');
    }
  };

  // Handle recharge credits
  const handleRecharge = async (userId) => {
    const credits = parseInt(rechargeAmount, 10);
    
    if (!credits || credits <= 0 || isNaN(credits)) {
      toast.error('Please enter a valid credits amount');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/recharge/${userId}`,
        {
          credits: credits,
          description: rechargeDescription || undefined
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data?.success) {
        toast.success(`Successfully recharged ${credits} credits`);
        setShowRechargeModal(false);
        setRechargeAmount('');
        setRechargeDescription('');
        setRechargingUserId(null);
        
        // Refresh users list to show updated credits
        await fetchUsers();
      } else {
        throw new Error(response.data?.error || 'Failed to recharge credits');
      }
    } catch (err) {
      console.error('Error recharging credits:', err);
      toast.error(err.response?.data?.error || 'Failed to recharge credits');
    }
  };

  // Open recharge modal
  const openRechargeModal = (userId) => {
    setRechargingUserId(userId);
    setRechargeAmount('');
    setRechargeDescription('');
    setShowRechargeModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      setCurrentPage(1);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = users.filter(user => 
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.id?.toString().includes(query)
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchQuery, users]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle admin wallet report download
  const handleDownloadWalletReport = async () => {
    try {
      setDownloadingWallet(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/report/download-wallet`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 30000
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `admin_wallet_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Admin wallet report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading admin wallet report:', error);
      toast.error('Failed to download wallet report. Please try again.');
    } finally {
      setDownloadingWallet(false);
    }
  };

  // Handle admin images report download
  const handleDownloadImagesReport = async () => {
    try {
      setDownloadingImages(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/report/download-images`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 30000
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `admin_images_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Admin images report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading admin images report:', error);
      toast.error('Failed to download images report. Please try again.');
    } finally {
      setDownloadingImages(false);
    }
  };

  if (!isAuthenticated()) {
    return null;
  }

  return (
    <div className="App admin-panel-page">
      <AppHeader 
        credits={walletInfo.credits} 
        generated={walletInfo.totalSpent || 0} 
        user={user} 
        onLogout={logout}
      />

      <main className="admin-main-content">
        <div className="admin-container">
          <div className="admin-header">
            <h1 className="admin-title">🔐 Admin Panel</h1>
            <div className="admin-header-actions">
              <div className="admin-report-buttons">
                <button
                  className="admin-btn admin-btn-report"
                  onClick={handleDownloadWalletReport}
                  disabled={downloadingWallet || downloadingImages}
                  title="Download wallet report for all users"
                >
                  {downloadingWallet ? (
                    <>🔄 Downloading...</>
                  ) : (
                    <>💳 Download Wallet Report</>
                  )}
                </button>
                <button
                  className="admin-btn admin-btn-report"
                  onClick={handleDownloadImagesReport}
                  disabled={downloadingWallet || downloadingImages}
                  title="Download images report for all users"
                >
                  {downloadingImages ? (
                    <>🔄 Downloading...</>
                  ) : (
                    <>🖼️ Download Images Report</>
                  )}
                </button>
              </div>
              <button 
                className="admin-refresh-btn"
                onClick={fetchUsers}
                disabled={loading}
              >
                {loading ? '🔄 Refreshing...' : '🔄 Refresh'}
              </button>
            </div>
          </div>

          {error && (
            <div className="admin-error">
              <p>{error}</p>
            </div>
          )}

          {loading ? (
            <div className="admin-loading">
              <div className="loading-spinner-large"></div>
              <p>Loading users...</p>
            </div>
          ) : (
            <>
              {/* Search and Filters */}
              <div className="admin-filters">
                <div className="admin-search-container">
                  <input
                    type="text"
                    placeholder="Search by name, email, or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="admin-search-input"
                  />
                  <span className="admin-search-icon">🔍</span>
                </div>
                <div className="admin-pagination-info">
                  <label htmlFor="items-per-page">Items per page:</label>
                  <select
                    id="items-per-page"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="admin-select"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>

              {/* Users Table */}
              <div className="admin-users-table-container">
                <table className="admin-users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Credits</th>
                      <th>Total Spent</th>
                      <th>Images Generated</th>
                      <th>Created</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="admin-no-users">
                          {searchQuery ? `No users found matching "${searchQuery}"` : 'No users found'}
                        </td>
                      </tr>
                    ) : (
                      paginatedUsers.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="admin-user-info">
                              {user.avatar && (
                                <img 
                                  src={user.avatar} 
                                  alt={user.name}
                                  className="admin-user-avatar"
                                />
                              )}
                              <span>{user.name}</span>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td className="admin-credits">{user.credits}</td>
                          <td className="admin-spent">{user.totalSpent}</td>
                          <td>{user.imagesGenerated}</td>
                          <td>{formatDate(user.createdAt)}</td>
                          <td>{formatDate(user.lastLogin)}</td>
                          <td>
                            <div className="admin-actions">
                              <button
                                className="admin-btn admin-btn-login"
                                onClick={() => handleLoginAs(user.id)}
                                title="Login as this user - Click to impersonate this user account"
                              >
                                🔑 Login As
                              </button>
                              <button
                                className="admin-btn admin-btn-recharge"
                                onClick={() => openRechargeModal(user.id)}
                                title="Recharge credits for this user"
                              >
                                💰 Recharge
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="admin-pagination">
                  <div className="admin-pagination-info-text">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
                  </div>
                  <div className="admin-pagination-controls">
                    <button
                      className="admin-pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      title="Previous page"
                    >
                      ← Previous
                    </button>
                    <div className="admin-pagination-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        // Show first page, last page, current page, and pages around current
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 2 && page <= currentPage + 2)
                        ) {
                          return (
                            <button
                              key={page}
                              className={`admin-pagination-number ${currentPage === page ? 'active' : ''}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 3 || page === currentPage + 3) {
                          return <span key={page} className="admin-pagination-ellipsis">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      className="admin-pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      title="Next page"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Recharge Modal */}
      {showRechargeModal && (
        <div className="admin-modal-overlay" onClick={() => setShowRechargeModal(false)}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Recharge Credits</h2>
              <button 
                className="admin-modal-close"
                onClick={() => setShowRechargeModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label htmlFor="recharge-credits">Credits Amount</label>
                <input
                  id="recharge-credits"
                  type="number"
                  min="1"
                  value={rechargeAmount}
                  onChange={(e) => setRechargeAmount(e.target.value)}
                  placeholder="Enter credits amount"
                  className="admin-input"
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="recharge-description">Description (Optional)</label>
                <textarea
                  id="recharge-description"
                  value={rechargeDescription}
                  onChange={(e) => setRechargeDescription(e.target.value)}
                  placeholder="Enter description for audit trail"
                  className="admin-textarea"
                  rows="3"
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-btn admin-btn-cancel"
                onClick={() => setShowRechargeModal(false)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-submit"
                onClick={() => handleRecharge(rechargingUserId)}
                disabled={!rechargeAmount || parseInt(rechargeAmount, 10) <= 0}
              >
                Recharge Credits
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;

